import { pickVariant, type SoundManifest, type SoundVariant } from "odyssey-shared";
import type { BufferLike, ContextLike } from "./graph.ts";

/**
 * The page's sounds (ADR-085 decisions 1, 2 and 8): the manifest's hooks, each variant's first encoding the browser
 * can play fetched and decoded once the context exists, the fallback a hook with no sound plays in its place, and the
 * variant each play picks, never the one played last for that hook while it has two or more.
 */
export class SoundBank {
  private manifest: SoundManifest = { sounds: {}, music: {} };
  private base = "";
  private readonly buffers = new Map<string, (BufferLike | null)[]>();
  private readonly last = new Map<string, number>();
  private loading: Promise<void> | null = null;

  /**
   * @param fetchBytes Fetches a URL's bytes.
   * @param canPlay Whether the browser plays a media type.
   * @param fallbacks Each hook's fallback, from `soundHooks`.
   * @param random The roll a variant is picked by, from 0 to 1.
   * @param onDecoded Told each time a variant has decoded.
   */
  public constructor(
    private readonly fetchBytes: (url: string) => Promise<ArrayBuffer>,
    private readonly canPlay: (type: string) => boolean,
    private readonly fallbacks: ReadonlyMap<string, string>,
    private readonly random: () => number,
    private readonly onDecoded: () => void = () => undefined,
  ) {}

  /**
   * Takes the manifest the page loaded and where its paths are read from.
   *
   * @param manifest The manifest.
   * @param base The asset base the manifest's paths are relative to.
   */
  public setManifest(manifest: SoundManifest, base: string): void {
    this.manifest = manifest;
    this.base = base;
    this.buffers.clear();
    this.last.clear();
    this.loading = null;
  }

  /**
   * The manifest held.
   *
   * @returns The manifest.
   */
  public get sounds(): SoundManifest {
    return this.manifest;
  }

  /**
   * The URL of the first encoding of a variant or track the browser can play.
   *
   * @param variant The variant or track.
   * @returns The URL, or null when the browser plays none of its encodings.
   */
  public urlOf(variant: SoundVariant): string | null {
    const encoding = variant.encodings.find((candidate) => this.canPlay(candidate.type));
    return encoding === undefined ? null : `${this.base}/${encoding.path}`;
  }

  /**
   * Fetches and decodes every variant of every hook once; a variant that fails stays silent and is logged.
   *
   * @param context The context that decodes.
   * @returns A promise that settles when every variant has decoded or failed.
   */
  public load(context: ContextLike): Promise<void> {
    if (this.loading !== null) {
      return this.loading;
    }
    const jobs: Promise<void>[] = [];
    for (const hook of Object.keys(this.manifest.sounds)) {
      const variants = this.manifest.sounds[hook] ?? [];
      const decoded: (BufferLike | null)[] = variants.map(() => null);
      this.buffers.set(hook, decoded);
      variants.forEach((variant, index) => {
        const url = this.urlOf(variant);
        if (url === null) {
          return;
        }
        jobs.push(
          this.fetchBytes(url)
            .then((bytes) => context.decodeAudioData(bytes))
            .then((buffer) => {
              decoded[index] = buffer;
              this.onDecoded();
            })
            .catch((error: unknown) => {
              console.warn(`the sound ${url} did not load; ${hook} plays without it`, error);
            }),
        );
      });
    }
    this.loading = Promise.all(jobs).then(() => undefined);
    return this.loading;
  }

  /**
   * The hook that sounds for a hook: itself when the manifest holds it, otherwise its fallbacks in turn.
   *
   * @param hook The hook asked for.
   * @returns The hook with a sound, or null when none in the chain has one.
   */
  public resolve(hook: string): string | null {
    let current: string | undefined = hook;
    for (let step = 0; current !== undefined && step < 4; step += 1) {
      if (this.manifest.sounds[current] !== undefined) {
        return current;
      }
      current = this.fallbacks.get(current);
    }
    return null;
  }

  /**
   * Whether the manifest holds a sound of the hook itself, whatever it falls back to.
   *
   * @param hook The hook.
   * @returns True when the hook has a file of its own.
   */
  public holds(hook: string): boolean {
    return this.manifest.sounds[hook] !== undefined;
  }

  /**
   * Picks a decoded variant of a hook with a sound.
   *
   * @param hook A hook `resolve` returned.
   * @returns The buffer, or null when the variant picked has not decoded.
   */
  public pick(hook: string): BufferLike | null {
    const decoded = this.buffers.get(hook);
    if (decoded === undefined || decoded.length === 0) {
      return null;
    }
    const index = pickVariant(decoded.length, this.last.get(hook) ?? -1, this.random());
    this.last.set(hook, index);
    return decoded[index] ?? null;
  }
}
